import { useState } from 'react';
import { TrendingDown, Users, Sparkles } from 'lucide-react';
import { KORA, type CompetitorData } from '@/routes/compare/data';

interface PricingCalculatorProps {
  data: CompetitorData;
}

const TEAM_SIZES = [1, 3, 5, 10, 15, 25, 50];

export function PricingCalculator({ data }: PricingCalculatorProps) {
  const [agents, setAgents] = useState(15);

  // Kora is usage-based, so cost is an estimate at typical volume:
  // ~30 tickets + 10 AI answers per agent/month = 40 credits = $4/agent/mo.
  const isFree = false;
  const koraCost = KORA.costAt(agents);
  const koraPlanName = 'Pay-as-you-go';

  // competitor cost: listed per-agent price (annual billing where the vendor lists it) × agents
  const competitorCost = Math.round(data.costAt(agents));
  const hasCompetitorPrice = competitorCost > 0;
  const savings = competitorCost - koraCost;
  const maxCost = Math.max(koraCost, competitorCost, 1);

  // Bar widths as percentages (min 2% so free bar is still visible)
  const koraBarWidth = `${Math.max((koraCost / maxCost) * 100, 2)}%`;
  const competitorBarWidth = `${(competitorCost / maxCost) * 100}%`;

  return (
    <section className="py-16 md:py-20 border-t border-zinc-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-2">
            Pricing at your team size
          </h2>
          <p className="text-sm text-zinc-500">
            Kora is pay-as-you-go ($1 = 10 credits, 1 credit per ticket). The Kora figure is an illustration that assumes 30 tickets and 10 AI answers per agent per month; use the cost calculator for your own volume. {data.name} charges per agent. Drag the slider to compare.
          </p>
        </div>

        {/* Controls */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-3">
              <Users className="size-5 text-zinc-400" />
              <div>
                <div className="text-sm font-medium text-zinc-900">
                  {agents} {agents === 1 ? 'agent' : 'agents'}
                </div>
                <div className="text-xs text-zinc-400">Team size</div>
              </div>
            </div>

          </div>

          {/* Slider */}
          <input
            type="range"
            min={0}
            max={TEAM_SIZES.length - 1}
            step={1}
            value={TEAM_SIZES.indexOf(agents)}
            onChange={(e) => setAgents(TEAM_SIZES[parseInt(e.target.value)])}
            className="w-full h-2 bg-zinc-100 rounded-full appearance-none cursor-pointer compare-slider"
            aria-label="Team size"
          />
          <div className="flex justify-between mt-2 px-1">
            {TEAM_SIZES.map((size) => (
              <button
                key={size}
                onClick={() => setAgents(size)}
                className={`text-[10px] transition-colors ${agents === size
                  ? 'text-zinc-900 font-medium'
                  : 'text-zinc-400'
                  }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        {/* Bar chart comparison */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 md:p-8">
          <div className="space-y-6">
            {/* Kora bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <img width={16} height={16} src="/assets/logo_square.svg" alt="Kora logo" className="size-4" />
                  <span className="text-sm font-medium text-zinc-900">
                    Kora {koraPlanName}
                  </span>
                  <span className="text-[10px] text-zinc-400">est.</span>
                </div>
                <span className="text-sm font-semibold text-zinc-900">
                  ${koraCost}/mo
                </span>
              </div>
              <div className="h-8 bg-zinc-100 rounded-lg overflow-hidden">
                <div
                  className={`h-full rounded-lg transition-all duration-500 ease-out ${isFree
                    ? 'bg-gradient-to-r from-green-400 to-green-500'
                    : 'bg-zinc-900'
                    }`}
                  style={{ width: koraBarWidth }}
                />
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                Illustration: 30 tickets + 10 AI answers per agent/mo · $1 = 10 credits · no per-seat costs · no monthly commitment
              </div>
            </div>

            {/* Competitor bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="size-4 rounded bg-zinc-200 flex items-center justify-center text-[8px] font-bold text-zinc-600">
                    {data.name[0]}
                  </div>
                  <span className="text-sm font-medium text-zinc-900">
                    {data.name} {data.plans.find((p) => p.highlight)?.name || data.plans[1]?.name}
                  </span>
                </div>
                <span className="text-sm font-semibold text-zinc-900">
                  ${competitorCost}/mo
                </span>
              </div>
              <div className="h-8 bg-zinc-100 rounded-lg overflow-hidden">
                <div
                  className="h-full bg-zinc-300 rounded-lg transition-all duration-500 ease-out"
                  style={{ width: competitorBarWidth }}
                />
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                {hasCompetitorPrice
                  ? `$${data.plans.find((p) => p.highlight)?.price}/agent/mo list price × ${agents} agents (checked on the vendor's pricing page; confirm before you buy)`
                  : 'No per-agent list price published'}
              </div>
            </div>
          </div>

          {/* Savings callout */}
          {hasCompetitorPrice && savings > 0 && (
            <div className="mt-6 pt-6 border-t border-zinc-100 flex items-center gap-3">
              <div className="size-9 rounded-xl bg-green-50 flex items-center justify-center">
                <TrendingDown className="size-4 text-green-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-zinc-900">
                  At this illustration Kora costs ${savings}/mo less
                </div>
                <div className="text-xs text-zinc-400">
                  About ${savings * 12}/year, if your volume matches the illustration.
                </div>
              </div>
            </div>
          )}
          {!hasCompetitorPrice && (
            <div className="mt-6 pt-6 border-t border-zinc-100 text-sm text-zinc-600">
              {data.name} does not publish a per-agent list price, so we cannot compare costs here.
            </div>
          )}
          {hasCompetitorPrice && savings <= 0 && (
            <div className="mt-6 pt-6 border-t border-zinc-100 flex items-center gap-3">
              <div className="size-9 rounded-xl bg-green-50 flex items-center justify-center">
                <Sparkles className="size-4 text-green-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-zinc-900">
                  At {agents} {agents === 1 ? 'agent' : 'agents'} the list prices are close
                </div>
                <div className="text-xs text-zinc-400">
                  {data.name} costs about ${competitorCost}/mo at the same team size. Your own ticket volume decides which is cheaper.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Plan cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* How Kora is priced */}
          <div className="rounded-xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
              <img width={16} height={16} src="/assets/logo_square.svg" alt="Kora logo" className="size-4" />
              <span className="text-sm font-semibold text-zinc-900">How Kora is priced</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500">Ticket created</span>
                <span className="text-zinc-900">1 credit · $0.10</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500">Members</span>
                <span className="text-zinc-900 font-medium">Unlimited, free</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500">To start</span>
                <span className="text-zinc-900">1,100 free credits, no card</span>
              </div>
            </div>
          </div>

          {/* Competitor plans */}
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="size-4 rounded bg-zinc-200 flex items-center justify-center text-[8px] font-bold text-zinc-600">
                {data.name[0]}
              </div>
              <span className="text-sm font-semibold text-zinc-900">{data.name} plans</span>
            </div>
            <div className="space-y-2">
              {data.plans.map((plan) => (
                <div key={plan.name} className="flex items-center justify-between text-xs">
                  <span className="text-zinc-500">
                    {plan.name}
                    {plan.highlight && <span className="ml-1 text-blue-500">·</span>}
                  </span>
                  <span className="text-zinc-900">
                    {plan.price === -1 ? 'Contact us' : plan.price === 0 ? 'Free' : `$${plan.price}/agent`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
